import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '@/components/Header';
import { useTeam } from '@/contexts/TeamContext';
import {
  Pulse, User, ChatCircle, UserPlus, CheckCircle,
  PencilSimple, Lightning, Briefcase, ArrowRight, SpinnerGap
} from '@phosphor-icons/react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const API = `${window.location.origin}/api`;

const ACTION_CONFIG = {
  team_created: { icon: Lightning, color: 'text-navy', bg: 'bg-blue-50', label: 'created the team' },
  member_invited: { icon: UserPlus, color: 'text-amber-600', bg: 'bg-amber-50', label: 'invited' },
  member_joined: { icon: UserPlus, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'joined the team' },
  member_role_changed: { icon: PencilSimple, color: 'text-blue-600', bg: 'bg-blue-50', label: "changed a member's role" },
  member_deactivated: { icon: User, color: 'text-red-500', bg: 'bg-red-50', label: 'deactivated a member' },
  task_created: { icon: CheckCircle, color: 'text-navy', bg: 'bg-blue-50', label: 'created a task' },
  task_assigned: { icon: ArrowRight, color: 'text-blue-600', bg: 'bg-blue-50', label: 'assigned a task' },
  task_reassigned: { icon: ArrowRight, color: 'text-amber-600', bg: 'bg-amber-50', label: 'reassigned a task' },
  tasks_bulk_reassigned: { icon: ArrowRight, color: 'text-amber-600', bg: 'bg-amber-50', label: 'bulk reassigned tasks' },
  matter_created: { icon: Briefcase, color: 'text-navy', bg: 'bg-blue-50', label: 'created a matter' },
  comment_added: { icon: ChatCircle, color: 'text-navy', bg: 'bg-slate-50', label: 'commented on' },
  approval_submitted: { icon: Lightning, color: 'text-amber-600', bg: 'bg-amber-50', label: 'submitted for approval' },
  approval_approved: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'approved' },
  approval_rejected: { icon: CheckCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'rejected' },
};

function ActivityItem({ entry, index }) {
  const config = ACTION_CONFIG[entry.action_type] || { icon: Pulse, color: 'text-slate-500', bg: 'bg-slate-50', label: entry.action_type };
  const Icon = config.icon;

  const timeAgo = (() => {
    try { return formatDistanceToNow(parseISO(entry.created_at), { addSuffix: true }); }
    catch { return ''; }
  })();

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0"
      data-testid={`activity-${entry.log_id}`}
    >
      <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <Icon size={15} weight="duotone" className={config.color} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700">
          <strong className="font-semibold">{entry.actor_name || 'Someone'}</strong>{' '}
          {config.label}{' '}
          {entry.entity_name && <strong className="font-medium">{entry.entity_name}</strong>}
        </p>
        <p className="text-[11px] text-slate-400 mt-0.5">{timeAgo}</p>
      </div>
    </motion.div>
  );
}

function TeamActivity({ user }) {
  const { activeTeam } = useTeam();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const teamId = activeTeam?.team_id;

  const fetchActivity = useCallback(async () => {
    if (!teamId) return;
    try {
      const res = await axios.get(`${API}/teams/${teamId}/activity?limit=100`, { withCredentials: true });
      setEntries(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [teamId]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  if (!activeTeam) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header user={user} />
        <main className="max-w-2xl mx-auto p-8 text-center pt-16">
          <p className="text-slate-500">Switch to a team workspace to view activity.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      <main className="max-w-3xl mx-auto p-6 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-slate-900 flex items-center gap-3">
            <Pulse size={28} weight="duotone" className="text-navy" />
            Activity Feed
          </h1>
          <p className="text-sm text-slate-500 mt-1">Recent activity in {activeTeam.team_name}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><SpinnerGap size={28} className="text-navy animate-spin" /></div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
            <Pulse size={36} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No activity yet. Actions from your team will appear here.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="activity-feed">
            {entries.map((e, i) => (
              <ActivityItem key={e.log_id} entry={e} index={i} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default TeamActivity;
